import {
  FileInfoDto,
  GDTMessageDirection,
  GDTMessageDto,
} from '@lmt/shared-types';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import {
  FullHeightGridRoot,
  HideNullTitlesHeaderContent,
  HoverableTable,
  ScrollTableOnScreenToTop,
  StickyHeaderRow,
  useColumnResizingState,
  useConvertedSorting,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { Paper } from '@mui/material';
import { snakeCase } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { GDTMessageInfo } from './common';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import {
  DXRG,
  SortingState,
  Table,
  TableColumnWidthInfo,
  TableRowDetailBase,
  useReactDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { HL7DocumentItem } from '../../../hl7/messages/components/DocumentItem';
import { getFullName } from '../../../patients/patient-form/common/common';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';

function dateToFormat(date: Date): string {
  return localFormat(date, 'Pp');
}

function getFormattedDateTime(date?: Date | null): string {
  return date ? dateToFormat(date) : '';
}

type Props = {
  noResults: boolean;
  data: GDTMessageDto[];
  documents: FileInfoDto[];
  sorting: { sortBy?: string | null; desc?: boolean };
  changeSorting: (sorting: { sortBy?: string | null; desc?: boolean }) => void;
  onDelete: (id: number) => void;
  onSave: (message: GDTMessageInfo) => void;
};

const TableKeys: { [dictionary: string]: string } = {
  id: 'id',
  dateTime: 'dateTime',
  direction: 'direction',
  status: 'status',
  patient: 'patient',
  document: 'document',
  error: 'error',
  actions: 'actions',
};

const RowDetail = (props: TableRowDetailBase.ContentProps) => (
  <div className={styles.content}>{props.row.content}</div>
);

export const GDTMessagesTable: FC<Props> = React.memo(
  function GDTMessagesTable(props) {
    useReactDataGrid();
    const localizationMessages = useReactDataGridLocalization();

    const i18n = useScopedTranslation('gdt.messages');
    const claims = useClaims();
    const tabs = useTabs();

    const openPatientProfile = useCallback((patientId: number) => {
      tabs.openTab(pages.patientProfile.link({ patientId }));
    }, []);

    const [expandedRowIds, setExpandedRowIds] = useState<(string | number)[]>(
      [],
    );

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [props.data]);

    const { sorting, setSorting } = useConvertedSorting(
      props.sorting,
      props.changeSorting,
      ScrollTableOnScreenToTop,
    );

    const defaultColumnWidths = useMemo<TableColumnWidthInfo[]>(
      () => [
        { columnName: TableKeys.id, width: '60px' },
        { columnName: TableKeys.dateTime, width: '155px' },
        { columnName: TableKeys.direction, width: '100px' },
        { columnName: TableKeys.status, width: '100px' },
        { columnName: TableKeys.patient, width: '200px' },
        { columnName: TableKeys.document, width: '200px' },
        { columnName: TableKeys.error, width: 'auto' },
        { columnName: TableKeys.actions, width: '50px' },
      ],
      [],
    );

    //we need to lock neighbouring columns, otherwise user can change the width of the whole table, negating our fix
    const { columnWidths, onColumnWidthsChange } = useColumnResizingState(
      defaultColumnWidths,
      [TableKeys.actions, TableKeys.error],
    );

    const tableColumns = useMemo(
      () => [
        {
          name: TableKeys.id,
          title: i18n.t(`columns.${TableKeys.id}` as any),
          getCellValue: (message: GDTMessageDto) => (
            <div className={styles.patientIdCell}>{message.id}</div>
          ),
        },
        {
          name: TableKeys.dateTime,
          title: i18n.t(`columns.${snakeCase(TableKeys.dateTime)}` as any),
          getCellValue: (message: GDTMessageDto) =>
            getFormattedDateTime(message.dateTime),
        },
        {
          name: TableKeys.direction,
          title: i18n.t(`columns.${TableKeys.direction}` as any),
          getCellValue: (message: GDTMessageDto) =>
            message.direction === GDTMessageDirection.Incoming
              ? i18n.t('incoming_message_status')
              : i18n.t('outgoing_message_status'),
        },
        {
          name: TableKeys.status,
          title: i18n.t(`columns.${TableKeys.status}` as any),
          getCellValue: (message: GDTMessageDto) =>
            i18n.t(`status.${message.status}` as any),
        },
        {
          name: TableKeys.patient,
          title: i18n.t(`columns.${TableKeys.patient}` as any),
          getCellValue: (message: GDTMessageDto) => (
            <div
              onClick={(event: any) => {
                event.stopPropagation();
                message.patient && openPatientProfile(message.patient.id);
              }}
              className={styles.patientNameInfo}
            >
              {getFullName(message.patient)}
            </div>
          ),
        },
        {
          name: TableKeys.document,
          title: i18n.t(`columns.${TableKeys.document}` as any),
          getCellValue: (message: GDTMessageDto) => (
            <div onClick={(event: any) => event.stopPropagation()}>
              <HL7DocumentItem
                document={props.documents.find(
                  (d) => d.id === message.outgoingFileId,
                )}
              />
            </div>
          ),
        },
        {
          name: TableKeys.error,
          title: i18n.t(`columns.${TableKeys.error}` as any),
          getCellValue: (message: GDTMessageDto) => message.error,
        },
        {
          name: TableKeys.actions,
          title: '',
          getCellValue: (message: GDTMessageDto) => (
            <div onClick={(event: any) => event.stopPropagation()}>
              {claims.permissions.gdtManagement && (
                <TableDotMenu
                  options={[
                    {
                      title: i18n.t('options.save_file'),
                      onClick: () => props.onSave(message),
                    },
                    {
                      title: i18n.t('options.delete'),
                      onClick: () => props.onDelete(message.id),
                      isDeleteItem: true,
                      isUnderLine: true,
                    },
                  ]}
                />
              )}
            </div>
          ),
        },
      ],
      [claims, props.documents],
    );

    const tableColumnExtensions = useMemo<Table.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.id },
        { columnName: TableKeys.dateTime, wordWrapEnabled: true },
        { columnName: TableKeys.direction },
        { columnName: TableKeys.status },
        { columnName: TableKeys.patient },
        { columnName: TableKeys.error, wordWrapEnabled: true },
        { columnName: TableKeys.actions },
      ],
      [],
    );

    const sortingColumnExtensions = useMemo<SortingState.ColumnExtension[]>(
      () => [
        { columnName: TableKeys.id, sortingEnabled: true },
        { columnName: TableKeys.dateTime, sortingEnabled: true },
        { columnName: TableKeys.direction, sortingEnabled: true },
      ],
      [],
    );

    const PressableRow = useMemo(
      () => (rowProps: Table.DataRowProps) => {
        function toggle() {
          const currentRowId = rowProps.row.id;
          if (expandedRowIds.includes(currentRowId)) {
            setExpandedRowIds(expandedRowIds.filter((x) => x != currentRowId));
          } else {
            setExpandedRowIds(expandedRowIds.concat([currentRowId]));
          }
        }

        return <DXRG.Table.Row {...rowProps} onClick={toggle} />;
      },
      [expandedRowIds],
    );

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18n.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <Paper style={{ height: '100%', width: '100%' }}>
        <DXRG.Grid
          rows={props.data ?? []}
          columns={tableColumns}
          getRowId={(message) => message.id}
          rootComponent={FullHeightGridRoot}
        >
          <DXRG.SortingState
            sorting={sorting}
            onSortingChange={setSorting}
            columnExtensions={sortingColumnExtensions}
            columnSortingEnabled={false}
          />
          <DXRG.RowDetailState
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={setExpandedRowIds}
          />
          <DXRG.Table
            messages={localizationMessages}
            columnExtensions={tableColumnExtensions}
            tableComponent={HoverableTable}
            rowComponent={PressableRow}
          />
          <DXRG.TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={onColumnWidthsChange}
            resizingMode={'nextColumn'}
          />
          <DXRG.TableHeaderRow
            showSortingControls
            contentComponent={HideNullTitlesHeaderContent}
            rowComponent={StickyHeaderRow}
          />
          <DXRG.TableRowDetail contentComponent={RowDetail} />
        </DXRG.Grid>
      </Paper>
    );
  },
);
